import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Save, CheckCircle } from 'lucide-react';
import { Spinner } from '../components/Spinner';
import styles from './SettingsPage.module.css';

interface AppliesTo {
  paintingKits: boolean;
  colorPalettes: boolean;
  customOrders: boolean;
  workshops: boolean;
}

interface PromotionForm {
  label: string;
  isEnabled: boolean;
  discountPercent: number;
  startsAt: string;
  endsAt: string;
  showDuration: boolean;
  appliesTo: AppliesTo;
}

const initialForm: PromotionForm = {
  label: '',
  isEnabled: false,
  discountPercent: 0,
  startsAt: '',
  endsAt: '',
  showDuration: true,
  appliesTo: {
    paintingKits: false,
    colorPalettes: false,
    customOrders: false,
    workshops: false,
  },
};

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getStatus(form: PromotionForm): { label: string; style: React.CSSProperties } {
  if (!form.isEnabled) {
    return { label: 'Disabled', style: { background: '#f3f4f6', color: '#6b7280' } };
  }
  if (!form.startsAt || !form.endsAt) {
    return { label: 'Not scheduled', style: { background: '#fef3c7', color: '#92400e' } };
  }
  const now = new Date();
  const start = new Date(form.startsAt);
  const end = new Date(form.endsAt);
  if (now < start) {
    return { label: 'Scheduled', style: { background: '#dbeafe', color: '#1e40af' } };
  }
  if (now > end) {
    return { label: 'Ended', style: { background: '#fee2e2', color: '#991b1b' } };
  }
  return { label: 'Active now', style: { background: '#d1fae5', color: '#065f46' } };
}

export function PromotionPage() {
  const { token } = useAuth();
  const [formData, setFormData] = useState<PromotionForm>(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const fetchPromotion = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promotion`);
      const data = await response.json();
      if (data.promotion) {
        setFormData({
          label: data.promotion.label || '',
          isEnabled: !!data.promotion.isEnabled,
          discountPercent: data.promotion.discountPercent || 0,
          startsAt: toDatetimeLocal(data.promotion.startsAt),
          endsAt: toDatetimeLocal(data.promotion.endsAt),
          showDuration: data.promotion.showDuration !== false,
          appliesTo: {
            paintingKits: !!data.promotion.appliesTo?.paintingKits,
            colorPalettes: !!data.promotion.appliesTo?.colorPalettes,
            customOrders: !!data.promotion.appliesTo?.customOrders,
            workshops: !!data.promotion.appliesTo?.workshops,
          },
        });
      }
    } catch (err) {
      console.error('Error fetching promotion:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotion();
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
    }));
    setSaved(false);
  };

  const toggleAppliesTo = (key: keyof AppliesTo) => {
    setFormData((prev) => ({
      ...prev,
      appliesTo: { ...prev.appliesTo, [key]: !prev.appliesTo[key] },
    }));
    setSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/promotion`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          label: formData.label,
          isEnabled: formData.isEnabled,
          discountPercent: formData.discountPercent,
          startsAt: formData.startsAt ? new Date(formData.startsAt).toISOString() : null,
          endsAt: formData.endsAt ? new Date(formData.endsAt).toISOString() : null,
          showDuration: formData.showDuration,
          appliesTo: formData.appliesTo,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save promotion');
      }
      setSaved(true);
      fetchPromotion();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Spinner label="Loading promotion settings..." size="lg" />;
  }

  const status = getStatus(formData);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1>Discount / Promotion</h1>
        <p>
          Controls the sitewide discount banner and pricing. Changes go live
          on the storefront immediately — no redeploy needed.
        </p>
      </header>

      <div className={styles.card}>
        {error && <div className={styles.error}>{error}</div>}
        {saved && (
          <div className={styles.success}>
            <CheckCircle size={18} />
            Saved — live on the storefront now.
          </div>
        )}

        <div
          style={{
            display: 'inline-flex',
            padding: '0.375rem 0.875rem',
            borderRadius: 9999,
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: '1.25rem',
            ...status.style,
          }}
        >
          {status.label}
        </div>

        <form onSubmit={handleSubmit}>
          <div className={styles.toggleRow}>
            <label htmlFor="isEnabled">Promotion Enabled</label>
            <label className={styles.switch}>
              <input
                type="checkbox"
                id="isEnabled"
                name="isEnabled"
                checked={formData.isEnabled}
                onChange={handleChange}
              />
              <span className={styles.switchSlider} />
            </label>
          </div>

          <div className={styles.formGroup}>
            <label>Banner Label</label>
            <input
              type="text"
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="e.g. Launch Sale — 15% off for 48 hours!"
            />
            <span className={styles.hint}>
              Shown in the red banner at the top of every storefront page.
            </span>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Discount Percent</label>
              <input
                type="number"
                name="discountPercent"
                value={formData.discountPercent}
                onChange={handleChange}
                min={0}
                max={100}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Starts At (your local time)</label>
              <input
                type="datetime-local"
                name="startsAt"
                value={formData.startsAt}
                onChange={handleChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label>Ends At (your local time)</label>
              <input
                type="datetime-local"
                name="endsAt"
                value={formData.endsAt}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className={styles.toggleRow}>
            <label htmlFor="showDuration">Show "Ends in ..." Countdown</label>
            <label className={styles.switch}>
              <input
                type="checkbox"
                id="showDuration"
                name="showDuration"
                checked={formData.showDuration}
                onChange={handleChange}
              />
              <span className={styles.switchSlider} />
            </label>
          </div>
          <span className={styles.hint}>
            When off, the storefront banner shows only the label text — no
            countdown timer.
          </span>

          <div className={styles.formGroup}>
            <label>Applies To</label>
            <div className={styles.checkboxGrid}>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.appliesTo.paintingKits}
                  onChange={() => toggleAppliesTo('paintingKits')}
                />
                Painting Kits
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.appliesTo.colorPalettes}
                  onChange={() => toggleAppliesTo('colorPalettes')}
                />
                Color Palettes
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.appliesTo.customOrders}
                  onChange={() => toggleAppliesTo('customOrders')}
                />
                Custom Orders
              </label>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={formData.appliesTo.workshops}
                  onChange={() => toggleAppliesTo('workshops')}
                />
                Workshops
              </label>
            </div>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={saving}>
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Promotion'}
          </button>
        </form>
      </div>
    </div>
  );
}
